import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { User } from '../models/user.model';
import { AuthUser, LoginRequest, LoginResponse } from '../models/auth.model';
import { environment } from '../../../environments/environment';

const STORAGE_KEY = 'mm_auth_user';
const TOKEN_KEY = 'mm_auth_token';

@Injectable({ providedIn: 'root' })
export class Auth {
  private readonly http = inject(HttpClient);
  private readonly userSubject = new BehaviorSubject<AuthUser | null>(this.loadUser());

  readonly user$ = this.userSubject.asObservable();

  private readonly infoProfileSubject = new BehaviorSubject<User | null>(null);
  readonly infoProfile$ = this.infoProfileSubject.asObservable();

  get currentUser(): AuthUser | null {
    return this.userSubject.value;
  }

  get infoProfile(): User | null {
    return this.infoProfileSubject.value;
  }

  get isLoggedIn(): boolean {
    return !!this.userSubject.value && !!this.getToken();
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/login`, { email, password } as LoginRequest)
      .pipe(
        tap((response) => {
          this.saveUser(response.user);
          this.saveToken(response.token);
          this.userSubject.next(response.user);
        })
      );
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/auth/logout`, {}).pipe(
      tap(() => {
        this.clearSession();
      })
    );
  }

  me(): Observable<{ user: AuthUser }> {
    return this.http.get<{ user: AuthUser }>(`${environment.apiUrl}/auth/me`).pipe(
      tap(({ user }) => {
        this.saveUser(user);
        this.userSubject.next(user);
      })
    );
  }

  getProfile(): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}/auth/profile`).pipe(
      tap((profile) => {
        this.infoProfileSubject.next(profile);
      })
    );
  }

  updateMe(data: Partial<User>): Observable<{ data: User }> {
    return this.http.patch<{ data: User }>(`${environment.apiUrl}/auth/update-me`, data);
  }

  updateUserData(data: Partial<User>): void {
    if (data) {
      const updatedUser = data as AuthUser;
      this.saveUser(updatedUser);
      this.userSubject.next(updatedUser);
    }
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/forgot-password`, {
      email,
    });
  }

  resetPassword(
    token: string,
    email: string,
    password: string,
    password_confirmation: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/reset-password`, {
      token,
      email,
      password,
      password_confirmation,
    });
  }

  acceptInvitation(
    token: string,
    email: string,
    password: string,
    password_confirmation: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/accept-invitation`, {
      token,
      email,
      password,
      password_confirmation,
    });
  }

  changePassword(
    current_password: string,
    password: string,
    password_confirmation: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${environment.apiUrl}/auth/change-password`, {
      current_password,
      password,
      password_confirmation,
    });
  }

  setAccessTokenMicrosoft(token: string): void {
    this.saveToken(token);
  }

  /**
   * Limpia la sesión local sin llamar al backend.
   * Útil cuando recibimos un 401 y queremos invalidar el token localmente.
   */
  clearLocalSession(): void {
    this.clearSession();
  }

  private clearSession(): void {
    this.userSubject.next(null);
    this.infoProfileSubject.next(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
  }

  private saveUser(user: AuthUser): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  }

  private saveToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private loadUser(): AuthUser | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  }

  loginMicrosoft(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/ms/login/microsoft`);
  }
}
